import { useState, useMemo } from 'react';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Download, Eye, Mail, FileText, Star, SlidersHorizontal, ArrowUpDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  brochures,
  popularKeywords,
  categoryLabels,
  industryLabels,
  type BrochureItem,
} from '@/data/technicalLibrary';

const Resources = () => {
  const { language, t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedFileType, setSelectedFileType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'relevance' | 'alpha'>('relevance');

  const filteredBrochures = useMemo(() => {
    let results = brochures.filter((item) => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !query ||
        item.title_el.toLowerCase().includes(query) ||
        item.title_en.toLowerCase().includes(query) ||
        item.description_el.toLowerCase().includes(query) ||
        item.description_en.toLowerCase().includes(query) ||
        item.keywords.some((k) => k.toLowerCase().includes(query));

      const matchesIndustry = selectedIndustry === 'all' || item.industry === selectedIndustry;
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchesFileType = selectedFileType === 'all' || item.file_type === selectedFileType;

      return matchesSearch && matchesIndustry && matchesCategory && matchesFileType;
    });

    if (sortBy === 'alpha') {
      results = [...results].sort((a, b) => {
        const titleA = language === 'el' ? a.title_el : a.title_en;
        const titleB = language === 'el' ? b.title_el : b.title_en;
        return titleA.localeCompare(titleB);
      });
    }

    return results;
  }, [searchQuery, selectedIndustry, selectedCategory, selectedFileType, sortBy, language]);

  const featuredBrochures = brochures.filter((b) => b.featured);

  const handleKeywordClick = (keyword: { el: string; en: string }) => {
    setSearchQuery(language === 'el' ? keyword.el : keyword.en);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedIndustry('all');
    setSelectedCategory('all');
    setSelectedFileType('all');
  };

  const hasActiveFilters =
    searchQuery || selectedIndustry !== 'all' || selectedCategory !== 'all' || selectedFileType !== 'all';

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={t('Τεχνικά Φυλλάδια', 'Technical Library')}
        description={t(
          'Αναζητήστε φυλλάδια, τεχνικά δελτία και εγχειρίδια εξοπλισμού βιομηχανίας τροφίμων.',
          'Search brochures, datasheets and manuals for food industry equipment.'
        )}
        canonical="https://www.tft.gr/resources"
      />
      <Navigation />

      {/* Hero */}
      <section className="relative bg-[hsl(var(--primary))] py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(220,80%,25%)] opacity-90" />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
            {t('Τεχνικά Φυλλάδια', 'Technical Library')}
          </h1>
          <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto">
            {t(
              'Βρείτε φυλλάδια, τεχνικά δελτία και εγχειρίδια από τους κορυφαίους κατασκευαστές εξοπλισμού τροφίμων.',
              'Find brochures, datasheets and manuals from leading food equipment manufacturers.'
            )}
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        {/* Search */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t(
                'Αναζήτηση τεχνικού υλικού, λέξεων-κλειδιών ή εφαρμογών...',
                'Search brochures, keywords or applications...'
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 text-base rounded-xl border-border shadow-sm"
            />
          </div>
        </div>

        {/* Popular Keywords */}
        <div className="max-w-3xl mx-auto mb-10 flex flex-wrap gap-2 justify-center">
          <span className="text-sm text-muted-foreground mr-1 self-center">
            {t('Δημοφιλή:', 'Popular:')}
          </span>
          {popularKeywords.map((kw, i) => (
            <Badge
              key={i}
              variant="outline"
              className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors text-sm px-3 py-1"
              onClick={() => handleKeywordClick(kw)}
            >
              {language === 'el' ? kw.el : kw.en}
            </Badge>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-8 items-start md:items-center">
          <div className="flex items-center gap-2 text-muted-foreground">
            <SlidersHorizontal className="h-4 w-4" />
            <span className="text-sm font-medium">{t('Φίλτρα:', 'Filters:')}</span>
          </div>
          <div className="flex flex-wrap gap-3 flex-1">
            <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
              <SelectTrigger className="w-[180px] rounded-lg">
                <SelectValue placeholder={t('Κλάδος', 'Industry')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('Όλοι οι κλάδοι', 'All Industries')}</SelectItem>
                {Object.entries(industryLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {language === 'el' ? label.el : label.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px] rounded-lg">
                <SelectValue placeholder={t('Κατηγορία', 'Category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('Όλες οι κατηγορίες', 'All Categories')}</SelectItem>
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {language === 'el' ? label.el : label.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedFileType} onValueChange={setSelectedFileType}>
              <SelectTrigger className="w-[160px] rounded-lg">
                <SelectValue placeholder={t('Τύπος', 'File Type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('Όλοι οι τύποι', 'All Types')}</SelectItem>
                <SelectItem value="PDF">PDF</SelectItem>
                <SelectItem value="Brochure">{t('Φυλλάδιο', 'Brochure')}</SelectItem>
                <SelectItem value="Datasheet">{t('Τεχνικό Δελτίο', 'Datasheet')}</SelectItem>
                <SelectItem value="Manual">{t('Εγχειρίδιο', 'Manual')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'relevance' | 'alpha')}>
              <SelectTrigger className="w-[160px] rounded-lg">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">{t('Σχετικότητα', 'Relevance')}</SelectItem>
                <SelectItem value="alpha">{t('Αλφαβητικά', 'Alphabetical')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              {t('Καθαρισμός', 'Clear')}
            </Button>
          )}
        </div>


        {/* Results */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {filteredBrochures.length} {t('αποτελέσματα', 'results')}
          </p>
        </div>

        {filteredBrochures.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBrochures.map((item) => (
              <BrochureCard key={item.id} item={item} language={language} t={t} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <FileText className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">
              {t('Δεν βρέθηκαν αποτελέσματα.', 'No results found.')}
            </p>
            <Button variant="outline" className="mt-4" onClick={clearFilters}>
              {t('Καθαρισμός φίλτρων', 'Clear filters')}
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

const BrochureCard = ({
  item,
  language,
  t,
  featured,
}: {
  item: BrochureItem;
  language: 'el' | 'en';
  t: (el: string, en: string) => string;
  featured?: boolean;
}) => {
  const title = language === 'el' ? item.title_el : item.title_en;
  const description = language === 'el' ? item.description_el : item.description_en;
  const industryLabel = industryLabels[item.industry];
  const catLabel = categoryLabels[item.category];

  return (
    <Card
      className={`group hover:shadow-lg transition-all duration-300 rounded-xl border-border ${
        featured ? 'ring-1 ring-primary/20' : ''
      }`}
    >
      <CardContent className="p-6 flex flex-col h-full">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="default" className="text-xs">
            {industryLabel ? (language === 'el' ? industryLabel.el : industryLabel.en) : item.industry}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {catLabel ? (language === 'el' ? catLabel.el : catLabel.en) : item.category}
          </Badge>
          <Badge variant="outline" className="text-xs">
            {item.file_type}
          </Badge>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
          {title}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-3 flex-1">{description}</p>

        {/* Keywords */}
        <div className="flex flex-wrap gap-1 mb-4">
          {item.keywords.slice(0, 3).map((kw, i) => (
            <span key={i} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {kw}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-auto">
          <Button size="sm" variant="default" className="flex-1 gap-1.5" asChild>
            <a href={item.file_url} target="_blank" rel="noopener noreferrer">
              <Eye className="h-3.5 w-3.5" />
              {t('Προβολή', 'View')}
            </a>
          </Button>
          <Button size="sm" variant="outline" className="flex-1 gap-1.5" asChild>
            <a href={item.file_url} download>
              <Download className="h-3.5 w-3.5" />
              {t('Λήψη', 'Download')}
            </a>
          </Button>
          <Button size="sm" variant="ghost" className="gap-1.5" asChild>
            <Link to="/contact">
              <Mail className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Resources;
